import { c as _c } from "react/compiler-runtime";
import { Check, Download, ExternalLink, FileText, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type SellerDocument } from "@/api";
import { sellerKeys, useSellerDocuments } from "@/api/hooks/use-sellers";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@osmix/ui/dialog";

// Э7: панель «Личное дело» продавца — документы верификации (миграция 044),
// keyed by userId. Показывает список загруженных документов (тип, имя, размер,
// статус, дата), даёт открыть/скачать файл (через object URL, как reports/media)
// и промодерировать документ («Принять»/«Отклонить» с примечанием). Рендерится
// внутри <ShopModeration>, поэтому доступна и на деталях продавца, и на витрине.

const STATUS_VARIANT: Record<SellerDocument["status"], "secondary" | "outline" | "destructive" | "default"> = {
  uploaded: "default",
  accepted: "secondary",
  rejected: "destructive"
};
const formatBytes = (bytes: number): string => {
  if (!bytes || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} Б`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} КБ`;
  return `${(kb / 1024).toFixed(1)} МБ`;
};
export function SellerDocumentsPanel({
  userId
}: {
  userId: string;
}) {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const docsQuery = useSellerDocuments(userId);
  const documents = docsQuery.data?.documents ?? [];
  const [viewing, setViewing] = useState<SellerDocument | null>(null);
  // Ревью документа: держим сам документ, действие и примечание.
  const [review, setReview] = useState<{
    doc: SellerDocument;
    status: "accepted" | "rejected";
  } | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const error = actionError ?? (docsQuery.error ? docsQuery.error.message : null);
  const submitReview = async () => {
    if (!review) return;
    if (review.status === "rejected" && !note.trim()) {
      setActionError(t("sellers.personalFile.noteRequired"));
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await api.sellers.reviewDocument(review.doc.id, {
        status: review.status,
        note: note.trim() || undefined
      });
      // Обновляем список документов и деталь продавца — приём документа может
      // изменить бейдж «Проверенный продавец».
      await queryClient.invalidateQueries({
        queryKey: sellerKeys.documents(userId)
      });
      await queryClient.invalidateQueries({
        queryKey: sellerKeys.detail(userId)
      });
      await queryClient.invalidateQueries({
        queryKey: sellerKeys.all
      });
      setReview(null);
      setNote("");
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return <Card>
			<CardHeader>
				<CardTitle>{t("sellers.personalFile.title")}</CardTitle>
				<p className="text-sm text-muted-foreground">
					{t("sellers.personalFile.subtitle")}
				</p>
			</CardHeader>
			<CardContent className="space-y-3 text-sm">
				{error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-destructive">
						{error}
					</div>}

				{docsQuery.isPending && <span className="inline-flex items-center gap-2 text-muted-foreground">
						<Loader2 className="size-4 animate-spin" />
						{t("sellers.personalFile.loading")}
					</span>}

				{!docsQuery.isPending && documents.length === 0 && !error && <p className="text-muted-foreground">
						{t("sellers.personalFile.empty")}
					</p>}

				{documents.map(doc => <div key={doc.id} className="flex flex-col gap-3 rounded-md border p-3 md:flex-row md:items-center md:justify-between">
						<div className="min-w-0 space-y-1">
							<button type="button" onClick={() => setViewing(doc)} className="inline-flex max-w-full items-center gap-2 text-left text-primary underline-offset-2 hover:underline">
								<FileText className="size-4 shrink-0" />
								<span className="truncate">{doc.filename}</span>
							</button>
							<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
								<span>{t(`sellers.personalFile.docType.${doc.docType}`)}</span>
								<span>·</span>
								<span>{formatBytes(doc.sizeBytes)}</span>
								<span>·</span>
								<span>{new Date(doc.createdAt).toLocaleDateString("ru-RU")}</span>
							</div>
							{doc.reviewNote && <p className="text-xs text-muted-foreground">
									{t("sellers.personalFile.noteLabel")}: {doc.reviewNote}
								</p>}
							{/* Автопроверка читаемости (П7, fd-onboard-32): пометка модератору. */}
							{doc.quality === "low" && <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs text-amber-800 dark:text-amber-300" data-testid="document-low-quality">
									{t("p7.documentLowQuality", {
              defaultValue: "Плохо читается"
            })}
									{doc.qualityNote ? `: ${doc.qualityNote}` : ""}
								</p>}
						</div>

						<div className="flex shrink-0 items-center gap-2">
							<Badge variant={STATUS_VARIANT[doc.status]}>
								{t(`sellers.personalFile.status.${doc.status}`)}
							</Badge>
							{doc.status !== "accepted" && <Button size="sm" variant="default" disabled={busy} onClick={() => {
            setNote("");
            setActionError(null);
            setReview({
              doc,
              status: "accepted"
            });
          }}>
									<Check className="size-4" />
									{t("sellers.personalFile.accept")}
								</Button>}
							{doc.status !== "rejected" && <Button size="sm" variant="outline" disabled={busy} onClick={() => {
            setNote("");
            setActionError(null);
            setReview({
              doc,
              status: "rejected"
            });
          }}>
									<X className="size-4" />
									{t("sellers.personalFile.reject")}
								</Button>}
						</div>
					</div>)}
			</CardContent>

			<ConfirmDialog open={!!review} busy={busy} title={review ? review.status === "accepted" ? t("sellers.personalFile.acceptTitle") : t("sellers.personalFile.rejectTitle") : ""} description={review ? review.status === "accepted" ? t("sellers.personalFile.acceptBody") : t("sellers.personalFile.rejectBody") : undefined} confirmLabel={review ? review.status === "accepted" ? t("sellers.personalFile.accept") : t("sellers.personalFile.reject") : undefined} confirmVariant={review?.status === "rejected" ? "destructive" : "default"} onConfirm={submitReview} onClose={() => {
      if (busy) return;
      setReview(null);
      setNote("");
    }}>
				<textarea value={note} onChange={e_0 => setNote(e_0.target.value)} placeholder={review?.status === "rejected" ? t("sellers.personalFile.notePlaceholderReject") : t("sellers.personalFile.notePlaceholderAccept")} className="min-h-24 w-full rounded-md border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
			</ConfirmDialog>

			<SellerDocumentViewer userId={userId} doc={viewing} onClose={() => setViewing(null)} />
		</Card>;
}

// Просмотр файла документа «личного дела». Приватный бакет не отдаёт публичный
// URL, поэтому байты тянем с bearer-заголовком и превращаем в object URL —
// PDF в <iframe>, картинки в <img>, остальное — открыть/скачать.
type Kind = "pdf" | "image" | "other";
const detectKind = (mime: string, filename?: string): Kind => {
  if (mime.includes("pdf") || filename?.toLowerCase().endsWith(".pdf")) return "pdf";
  if (mime.startsWith("image/")) return "image";
  return "other";
};
function SellerDocumentViewer(t0) {
  const $ = _c(55);
  const {
    userId,
    doc,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const [url, setUrl] = useState(null);
  const docId = doc?.id ?? "";
  let t1;
  if ($[0] !== docId || $[1] !== userId) {
    t1 = sellerKeys.documentFile(userId, docId);
    $[0] = docId;
    $[1] = userId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== docId || $[4] !== userId) {
    t2 = () => api.sellers.documentFile(userId, docId);
    $[3] = docId;
    $[4] = userId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = !!doc;
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data: blob,
    error
  } = useQuery(t4);
  let t5;
  if ($[10] !== doc) {
    t5 = doc ? detectKind(doc.mime, doc.filename) : "other";
    $[10] = doc;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const kind = t5;
  let t6;
  let t7;
  if ($[12] !== blob) {
    t6 = () => {
      if (!blob) {
        setUrl(null);
        return;
      }
      const objUrl = URL.createObjectURL(blob);
      setUrl(objUrl);
      return () => {
        URL.revokeObjectURL(objUrl);
      };
    };
    t7 = [blob];
    $[12] = blob;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  const filename = doc?.filename ?? "document";
  let t8;
  if ($[15] !== filename || $[16] !== url) {
    t8 = () => {
      if (!url) {
        return;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    };
    $[15] = filename;
    $[16] = url;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const download = t8;
  const t9 = !!doc;
  let t10;
  if ($[18] !== onClose) {
    t10 = open => !open && onClose();
    $[18] = onClose;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== filename) {
    t11 = <DialogHeader><DialogTitle className="truncate pr-8">{filename}</DialogTitle></DialogHeader>;
    $[20] = filename;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== error || $[23] !== filename || $[24] !== kind || $[25] !== t || $[26] !== url) {
    t12 = <div className="flex min-h-[60vh] items-center justify-center overflow-auto rounded-md border bg-muted/30">{error ? <p className="p-6 text-sm text-destructive">{t("sellers.docViewer.loadError")}</p> : !url ? <span className="inline-flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" />{t("sellers.docViewer.loading")}</span> : kind === "pdf" ? <iframe src={url} title={filename} className="h-[70vh] w-full" /> : kind === "image" ? <img src={url} alt={filename} className="max-h-[70vh] w-auto object-contain" /> : <p className="p-6 text-center text-sm text-muted-foreground">{t("sellers.docViewer.previewUnavailable")}</p>}</div>;
    $[22] = error;
    $[23] = filename;
    $[24] = kind;
    $[25] = t;
    $[26] = url;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = !url;
  let t14;
  if ($[28] !== url) {
    t14 = () => url && window.open(url, "_blank", "noopener");
    $[28] = url;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ExternalLink className="size-4" />;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t) {
    t16 = t("sellers.docViewer.openInTab");
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t13 || $[34] !== t14 || $[35] !== t16) {
    t17 = <Button variant="outline" disabled={t13} onClick={t14}>{t15}{t16}</Button>;
    $[33] = t13;
    $[34] = t14;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const t18 = !url;
  let t19;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Download className="size-4" />;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t) {
    t20 = t("sellers.docViewer.download");
    $[38] = t;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== download || $[41] !== t18 || $[42] !== t20) {
    t21 = <Button disabled={t18} onClick={download}>{t19}{t20}</Button>;
    $[40] = download;
    $[41] = t18;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t17 || $[45] !== t21) {
    t22 = <div className="flex justify-end gap-2">{t17}{t21}</div>;
    $[44] = t17;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t11 || $[48] !== t12 || $[49] !== t22) {
    t23 = <DialogContent className="max-w-3xl">{t11}{t12}{t22}</DialogContent>;
    $[47] = t11;
    $[48] = t12;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t10 || $[52] !== t23 || $[53] !== t9) {
    t24 = <Dialog open={t9} onOpenChange={t10}>{t23}</Dialog>;
    $[51] = t10;
    $[52] = t23;
    $[53] = t9;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  return t24;
}